/* eslint-disable no-console */
import {
  GoalLevel,
  GoalMetricOption,
  GoalPeriodType,
  MetricsOption,
  VerticalOption,
} from '@amzn/aet-galactus-kit';
import { ExceptionStatus } from '@amzn/aet-dynamo-kit';
import lodash, { cloneDeep } from 'lodash';
import moment from 'moment';

import {
  LighthouseFilters,
  LighthouseMetricOption,
  LighthouseMetrics,
  TimeOption,
  TRAILING_DAYS,
} from '../PerformancePortal/Dashboard/DashboardComponents/LighthouseMetrics/types';
import {
  ExtendedUser,
  InspireOptions,
  Profile,
  ReportingTeamUser,
  TeamProfile,
  ProxyRelatedUser,
  AssociateInsight,
  BottomQuartileMetric,
} from '../Types';
import {
  GetBatchCoachingStatsRequest,
  Period,
} from '@amzn/aet-coachingstats-kit';
import {
  GetBatchCoachingStatsAppSyncRequest,
  GetBatchCoachingStatsAppSyncResponse,
  GetCoachingStatsAppSyncRequest,
  GetCoachingStatsAppSyncResponse,
} from '../Types/CoachingStats';
import DataState from '../Types/DataState';
import { EmptyCoachingFormData } from '../Types/CoachingForm';
import { getCurrentWeek, getWeekAndYear, logger } from '../Util';
import { getUserIdWithPrefix } from '../Util/Helper';
import { makeMutation, makeQuery, NOTIFICATION_REQUEST } from './AppSyncClient';
import * as coachingMutations from './CoachingForm/mutations';
import * as coachingQueries from './CoachingForm/queries';
import {
  CoachingFormData,
  CoachingFormInputData,
  defaultCoachingSessionValues,
  DeliveredStatusObject,
  enumEmpty,
  AWSDateTime,
  EnumEmpty,
} from './CoachingForm/schema';
import * as contactQueries from './CoachingNotes/queries';
import * as coachingFormQueries from './CoachingForm/queries';
import * as txMonitoringQueries from '../commons/formLibrary/queries';
import {
  getCcxDetails,
  getContactStatusList,
  getCCXForContact as getCCXForContactQuery,
} from './CCXDetails/queries';
import {
  ContactDetails,
  ContactDetailsInput,
  ContactReviewPageInput,
  ContactStatusChangeInput,
  ContactViewTabType,
  FormAnswer,
} from './CoachingNotes/schema';
import * as dashboardMutations from './Dashboard/mutations';
import * as dashboardQueries from './Dashboard/queries';
import * as coachingCommentMutations from './CoachingComments/mutations';
import * as coachingCommentQueries from './CoachingComments/queries';
import * as notificationQueries from './Notifications/queries';
import * as notificationMutations from './Notifications/mutation';
import * as teamQueries from './Team/queries';
import * as contactListeningQueries from './ContactListening/queries';
import * as proxyMutations from './Proxy/mutations';
import * as proxyQueries from './Proxy/queries';
import { GetProxy } from './Proxy/schema';
import * as FormLibraryMutation from './FormLibrary/mutations';
import * as goalMutations from './Goals/mutations';
import * as goalQueries from './Goals/query';
import * as goalCommentsQueries from './GoalComments/query';
import * as goalCommentsMutations from './GoalComments/mutations';
import * as summariesQueries from './Summaries/query';
import * as summariesMutations from './Summaries/mutations';
import * as trainingQueries from './Training/queries';
import * as trainingMutations from './Training/mutations';
import { ProxyNotification } from '../Types/ProxyNotification';
import { Feature, isFeatureEnabled } from '../config/FeaturesConfig';
import { GetFeedbackMessagesInput } from './Dashboard/schema';
import { Channel, Status, StatusInput } from './Notifications/schema';
import { ContactListeningQueryTypeOption } from './ContactListening/schema';
import { UpdateFormReviewInput } from './FormLibrary/schema';
import * as outlierMutations from './Outlier/mutation';
import { getWriteOutlierExceptionMutation } from './Outlier/mutation';
import {
  getCoachingsAndContactReviewsQuery,
  getTeamDetailsMetricsQuery,
  getTrendsMetricsQuery,
} from './../PerformancePortal/Team/Table/TimeTrends/queries';
import {
  getBatchCoachingStatsQuery,
  getCoachingStatsQuery,
} from './CoachingStats/queries';
import { getAssociateInsightsQuery } from './Team/queries';
import { filterCCXNeg } from '../PerformancePortal/Team/Table/TimeTrends/helper';
import { TrendAndDetailsMetrics } from '../PerformancePortal/Team/Table/TimeTrends/types';
import { FetchPolicy } from '@apollo/client';
import { WeeklySummary } from '@amzn/aet-summaries-kit';
import {
  ManagerFeedbackInput,
  TeamCSAWeeklySummary,
} from '../PerformancePortal/CSASummaries/types';
import {
  TrainingStatsUserInput,
  TrainingStatsResult,
  GetTrainingCatalogInput,
  TrainingCatalogResult,
  AssignmentInput,
  TrainingAssignmentInput,
} from './Training/schema';
import Auth from '../Auth';

export type SimpleTeamProfileQueryVariables = {
  userID: string;
};

export type QueryVariables = {
  week: number;
  year: number;
  userID?: string;
  onlyDelivered?: boolean;
};

export interface QueryVariablesRange {
  startWeek: number;
  startYear: number;
  endWeek: number;
  endYear: number;
  userID: string;
}

export type QueryVariablesCustomerComments = (
  | QueryVariables
  | QueryVariablesRange
) & {
  enableProfanityFilter?: boolean;
};

interface QueryVariablesRangeRecommendationsGetter {
  userID: string;
  userName: string;
  startYear: number;
  startWeek: number;
  endYear: number;
  endWeek: number;
  timeOption?: TimeOption;
  timestamp?: string;
  isTM?: boolean;
}

interface GetFormTemplateVariables {
  FORM_ID?: string;
  VERSION?: string;
  VERTICAL?: string;
}

interface GetCCXDetailsVariables {
  channel: string;
  directReports: string[];
  isTM: boolean;
  segment: string;
  skill: string;
  marketplace?: string;
  syncMetrics: boolean;
  timeOption: TimeOption;
  timestamp: string;
  userID: string;
  vertical: VerticalOption;
}

export interface GetContactStatusVariables {
  users: string[];
  startWeek: string;
  endWeek: string;
}

interface GetCCXForContactVariables {
  startDate: TimeOption;
  endDate: TimeOption;
  contactID: string;
  userID: string;
}

interface RemoveCoacheeInput {
  userID: string;
  coacheeID: string;
}

interface RemoveCoachInput {
  userID: string;
  coachID: string;
}

interface AddCoachInput {
  userID: string;
  username: string;
  ownerName: string;
}

interface RemoveProxyInput {
  userID: string;
  proxyID: string;
}

interface AddProxyInput {
  userID: string;
  proxyUsername: string;
  sharedTeam: string[];
}

interface ProxyRequestContext {
  userID: string;
  profileUserID: string;
  proxyCoaches: ProxyRelatedUser[];
  profileManagers: ExtendedUser[];
}

interface GetProxyNotificationInput {
  userID: string;
}

interface DeleteProxyNotificationInput {
  userID: string;
  relatedUserID: string[];
}

interface GetTeamProxyInput {
  userID: string;
  directReportUserIds: string[];
}

interface GetNotificationInput {
  windowStart: AWSDateTime;
  windowEnd: AWSDateTime;
  target?: String;
  channel: [Channel];
  statusCodes: [Status];
  clientName: String;
}

interface GetNotification {
  input: GetNotificationInput;
}

interface TeamProfileDataQueryInput {
  userID: string;
}

interface UpdateNotificationStatusInput {
  clientName: String;
  notificationId: String;
  status: [StatusInput];
  metadata?: object;
}

interface UpdateNotification {
  input: UpdateNotificationStatusInput;
}

interface SendNotificationInput {
  userID: String;
  receiverId: String;
  receiverUsername: String;
  notificationType: String;
  notificationData?: String;
  emailTemplate?: String;
  locale?: String;
  inAppTitleId?: String;
  inAppMessageId?: String;
}

interface HideCustomerCommentInput {
  userID: string;
  contactId: string;
  responseDateTime: string;
}

interface GetCoachingCommentsInput {
  noteID: String;
  requesterAlias: String;
  requesterRole: String;
}

interface AddCoachingCommentInput {
  noteID: String;
  coachingID: String;
  authorAlias: String;
  comment: String;
  notificationReceivers: [String];
  owner: String;
  commentThreadType: String;
  receiverAlias: String;
}

export interface WriteOutlierExceptionInput {
  userId: String;
  deliveredToUsername: String;
  deliveredToUserId: String;
  contactId: String;
  outlierCalYear: String;
  outlierCalWeek: String;
  justification?: String;
}

export interface GetCoachingsAndContactReviewsInput {
  presetDate: string;
  reviewTypes: string[];
  directReports: UserInfo[];
}

interface UpdateOutlierExceptionInput {
  userId: String;
  deliveredToUsername: String;
  contactId: String;
  exceptionStatus: String;
  outlierCalYear: String;
}

export interface UserInfo {
  userName: string;
}

export interface DirectReport {
  userId: string;
  userName: string;
  userOwner: string;
}

export interface GoalInput {
  userID: string;
  userName: string;
  vertical: string;
  level: GoalLevel;
  description: string;
  metric: GoalMetricOption;
  targetValue: string;
  periodType: GoalPeriodType;
  periodValue: string;
  createdBy: string;
}

export interface CreateGoalInput {
  userID: string;
  input: GoalInput;
}

export interface GetGoalCommentsInput {
  userID: string;
  goalID: string;
}

export interface GetTeamLevelGoalsInput {
  userID: string;
}

export interface GetAssociateLevelGoalsInput {
  userID: string;
  associates: string[];
}

export interface GoalJustification {
  authorUserName: string;
  goalID: string;
  justification: string;
  userID: string;
}

export interface GoalJustificationInput {
  input: GoalJustification;
  userID: string;
}

export enum ReviewTypes {
  COACHING_NOTES = 'COACHING_NOTES',
  CONTACT_REVIEW = 'CONTACT_REVIEW',
}

export interface GetAssociateInsightsInput {
  tmID: string;
  associates: string[];
  associatesAliases: string[];
  timeOption: TimeOption;
  timestamp: string;
}

export interface GetTrainingStatsInput {
  userID: string;
  users: string[];
  startDate: string;
  endDate: string;
}

export const removeKeys = (obj: any, key: string) => {
  const removeKeysRecursive = (obj: any, key: string) => {
    // eslint-disable-next-line no-restricted-syntax
    for (const prop in obj) {
      // eslint-disable-next-line no-prototype-builtins
      if (obj.hasOwnProperty(prop)) {
        // eslint-disable-next-line default-case
        switch (typeof obj[prop]) {
          case 'string':
            if (prop === key) {
              // eslint-disable-next-line no-param-reassign
              delete obj[prop];
            }
            break;
          case 'object':
            if (prop === key) {
              // eslint-disable-next-line no-param-reassign
              delete obj[prop];
            } else {
              removeKeysRecursive(obj[prop], key);
            }
            break;
          default:
            break;
        }
      }
    }
  };

  const cloned = cloneDeep(obj);
  removeKeysRecursive(cloned, key);
  return cloned;
};

/** Start of Query Preparation Function */
const prepareQueryForGetNotification = (
  input: GetNotificationInput
): GetNotification => {
  return {
    input,
  };
};

const prepareQueryForUpdateNotification = (
  input: UpdateNotificationStatusInput
): UpdateNotification => {
  return {
    input,
  };
};

const prepareQueryForGetCoachingComments = (
  noteID: string,
  requesterAlias: string,
  requesterRole: string
): GetCoachingCommentsInput => {
  return {
    noteID,
    requesterAlias,
    requesterRole,
  };
};

const prepareQueryForAddCoachingComment = (
  noteID: String,
  coachingID: String,
  authorAlias: String,
  comment: String,
  notificationReceivers: [String],
  owner: String,
  commentThreadType: String,
  receiverAlias: String
): AddCoachingCommentInput => {
  return {
    noteID,
    coachingID,
    authorAlias,
    comment,
    notificationReceivers,
    owner,
    commentThreadType,
    receiverAlias,
  };
};

const prepareQueryForWriteOutlierException = (
  userId: String,
  deliveredToUsername: String,
  deliveredToUserId: String,
  contactId: String,
  outlierCalYear: String,
  outlierCalWeek: String,
  justification?: String
) => {
  const result = {
    userId,
    deliveredToUsername,
    deliveredToUserId,
    contactId,
    outlierCalYear,
    outlierCalWeek,
  };

  // Only include justification if it's not empty or undefined
  if (justification && justification.trim() !== '') {
    return {
      ...result,
      justification,
    };
  }

  return result;
};

const prepareQueryForUpdateOutlierException = (
  userId: String,
  deliveredToUsername: String,
  contactId: String,
  exceptionStatus: ExceptionStatus,
  outlierCalYear: String
): UpdateOutlierExceptionInput => {
  return {
    userId,
    deliveredToUsername,
    contactId,
    exceptionStatus,
    outlierCalYear,
  };
};

const prepareQueryForRemoveCoachee = (
  userID: string,
  coacheeID: string,
  cscEntityPrefix: string
): RemoveCoacheeInput => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
    coacheeID,
  };
};

const prepareQueryForRemoveCoach = (
  userID: string,
  coachID: string,
  cscEntityPrefix: string
): RemoveCoachInput => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
    coachID,
  };
};

const prepareQueryForAddCoach = (
  userID: string,
  username: string,
  ownerName: string,
  cscEntityPrefix: string
): AddCoachInput => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
    username,
    ownerName,
  };
};

const prepareQueryForRemoveProxy = (
  userID: string,
  proxyID: string,
  cscEntityPrefix: string
): RemoveProxyInput => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
    proxyID: proxyID ? `${cscEntityPrefix}:${proxyID}` : '',
  };
};

const prepareQueryForHideCustomerComment = (
  userID: string,
  contactId: string,
  responseDateTime: string
): HideCustomerCommentInput => {
  return {
    userID,
    contactId,
    responseDateTime,
  };
};

const prepareQueryForAddProxy = (
  userID: string,
  proxyUsername: string,
  cscEntityPrefix: string,
  sharedTeam: string[]
): AddProxyInput => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
    proxyUsername,
    sharedTeam,
  };
};

const prepareQueryForGetProxyNotification = (
  userID: string,
  cscEntityPrefix: string
): GetProxyNotificationInput => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
  };
};

const prepareQueryForDeleteProxyNotification = (
  userID: string,
  cscEntityPrefix: string,
  proxyNotifications: ProxyNotification[]
): DeleteProxyNotificationInput => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
    relatedUserID: proxyNotifications.map(
      (notification: ProxyNotification) => notification.relatedUserID
    ),
  };
};

const prepareSimpleTeamProfileQuery = (
  userID: string,
  cscEntityPrefix: string
): SimpleTeamProfileQueryVariables => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
  };
};

const prepareQueryForGetTeamProxy = (
  userID: string,
  directReportUserIds: string[],
  cscEntityPrefix: string
) => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
    directReportUserIds: directReportUserIds.map(
      id => `${cscEntityPrefix}:${id}`
    ),
  };
};

const prepareQuery = (
  selectedWeek: string,
  userID: string,
  cscEntityPrefix: string,
  firstWeek?: string,
  onlyDelivered?: boolean
): QueryVariables | QueryVariablesRange => {
  const { year, week } = getWeekAndYear(
    selectedWeek === TRAILING_DAYS ? getCurrentWeek() : selectedWeek
  );
  const { year: startYear, week: startWeek } = getWeekAndYear(
    firstWeek || selectedWeek
  );

  if (firstWeek) {
    return {
      startWeek,
      startYear,
      endWeek: week,
      endYear: year,
      userID: `${cscEntityPrefix}:${userID}`,
    };
  }

  if (onlyDelivered) {
    return {
      week,
      year,
      userID: `${cscEntityPrefix}:${userID}`,
      onlyDelivered,
    };
  }

  return {
    week,
    year,
    userID: `${cscEntityPrefix}:${userID}`,
  };
};

const prepareQueryForCustomerComment = (props: {
  selectedWeek: string;
  userID: string;
  cscEntityPrefix: string;
  firstWeek?: string;
  onlyDelivered?: boolean;
  enableProfanityFilter?: boolean;
}): QueryVariablesCustomerComments => {
  const {
    selectedWeek,
    userID,
    cscEntityPrefix,
    firstWeek,
    onlyDelivered,
    enableProfanityFilter,
  } = props;

  const queryVariables = prepareQuery(
    selectedWeek,
    userID,
    cscEntityPrefix,
    firstWeek,
    onlyDelivered
  );

  const queryVariablesCustomerComments: QueryVariablesCustomerComments = {
    ...queryVariables,
    enableProfanityFilter,
  };

  return queryVariablesCustomerComments;
};

const prepareQueryforRecommendationsGetter = (
  userid: string,
  username: string,
  startWeek: string,
  endWeek: string,
  timeOption?: TimeOption,
  timestamp?: string,
  isTM?: boolean
): QueryVariablesRangeRecommendationsGetter => {
  const { year: StartYear, week: StartWeek } = getWeekAndYear(startWeek);
  const { year: EndYear, week: EndWeek } = getWeekAndYear(endWeek);
  if (timeOption && timestamp && isTM !== undefined) {
    return {
      userID: userid,
      userName: username,
      startYear: StartYear,
      startWeek: StartWeek,
      endYear: EndYear,
      endWeek: EndWeek,
      timeOption: timeOption,
      timestamp: timestamp,
      isTM: isTM,
    };
  } else
    return {
      userID: userid,
      userName: username,
      startYear: StartYear,
      startWeek: StartWeek,
      endYear: EndYear,
      endWeek: EndWeek,
    };
};

const prepareQueryforGetCoachingsAndContactReviews = (
  presetDate: string,
  cscEntityPrefix: string,
  reviewTypes: string[],
  directReports: UserInfo[]
): GetCoachingsAndContactReviewsInput => {
  return {
    presetDate,
    reviewTypes,
    directReports: directReports,
  };
};

const prepareQueryForGetCoachingStats = (
  userId: string,
  periods: Period[]
): GetCoachingStatsAppSyncRequest => {
  return {
    request: {
      userId,
      periods,
    },
  };
};

export const prepareQueryForBatchGetCoachingStats = (
  tmDirectReportsOfOM: ReportingTeamUser[],
  periods: Period[],
  cscEntityPrefix: string
): GetBatchCoachingStatsAppSyncRequest => {
  const inputs = tmDirectReportsOfOM.map(tmUserId => {
    return {
      userId: getUserIdWithPrefix(cscEntityPrefix, tmUserId.relatedUser.userID),
      periods,
    };
  });

  const batchCoachingStatsRequest: GetBatchCoachingStatsAppSyncRequest = {
    inputs,
  };

  return batchCoachingStatsRequest;
};

const prepareQueryforGetTeamLevelGoals = (
  userID: string
): GetTeamLevelGoalsInput => {
  return {
    userID: userID,
  };
};

const prepareQueryforGetAssociateLevelGoals = (
  userID: string,
  associates: string[]
): GetAssociateLevelGoalsInput => {
  return {
    userID: userID,
    associates: associates,
  };
};

interface QueryVariablesProfile {
  userName: string;
  userID: string;
}

interface QueryVariablesOrgVertical {
  userIds: string[];
  userID: string;
}

const prepareAssociateQuery = (
  userName: string,
  userID: string,
  cscEntityPrefix: string
): QueryVariablesProfile => {
  return {
    userName,
    userID: `${cscEntityPrefix}:${userID}`,
  };
};

const prepareOrgVerticalQuery = (
  userIds: string[],
  userID: string
): QueryVariablesOrgVertical => {
  return {
    userIds,
    userID,
  };
};

interface QueryVariablesContact {
  noteID: string;
  userID?: string;
}
const prepareContactQuery = (
  noteID: string,
  userID: string,
  cscEntityPrefix: string
): QueryVariablesContact => {
  return {
    noteID,
    userID: `${cscEntityPrefix}:${userID}`,
  };
};

const prepareContactDetailsQuery = (
  contact: ContactReviewPageInput | ContactDetails,
  recommended: number,
  contactTab: ContactViewTabType | EnumEmpty,
  saved: number,
  saveOrigin: number
): ContactDetailsInput => {
  const newGreet: FormAnswer = {
    status: contact.greet?.status,
    note: contact.greet?.note,
  };
  const newVerifySecurity: FormAnswer = {
    status: contact.verifySecurity?.status,
    note: contact.verifySecurity?.note,
  };
  const newIdentifyProblem: FormAnswer = {
    status: contact.identifyProblem?.status,
    note: contact.identifyProblem?.note,
  };
  const newAcknowledge: FormAnswer = {
    status: contact.acknowledge?.status,
    note: contact.acknowledge?.note,
  };

  const newResearchMatch: FormAnswer = {
    status: contact.researchMatch?.status,
    note: contact.researchMatch?.note,
  };
  const newSolve: FormAnswer = {
    status: contact.solve?.status,
    note: contact.solve?.note,
  };
  const newClose: FormAnswer = {
    status: contact.close?.status,
    note: contact.close?.note,
  };

  const contactDetailsInput: ContactDetailsInput = {
    followedSecurityPolicies: contact.followedSecurityPolicies,
    vertical: contact.vertical,
    content: contact.content,
    relatedMetric: contact.relatedMetric,
    recommended: recommended,
    contactTab: contactTab,
    saved: saved,
    saveOrigin: saveOrigin,
    resolvedProblem: contact.resolvedProblem,
    controllable: contact.controllable,
    uncontrollableText: contact.uncontrollableText,
    greet: newGreet,
    verifySecurity: newVerifySecurity,
    identifyProblem: newIdentifyProblem,
    acknowledge: newAcknowledge,
    researchMatch: newResearchMatch,
    solve: newSolve,
    close: newClose,
    additionalComment: contact.additionalComment,
    goodRecommendation: contact.goodRecommendation,
    uncoachableReason: contact.uncoachableReason,
  };
  return contactDetailsInput;
};

const prepareQueryForGetAssociateInsights = (
  tmID: string,
  associates: string[],
  associatesAliases: string[],
  timeOption: TimeOption,
  timestamp: string
): GetAssociateInsightsInput => {
  return {
    tmID,
    associates,
    associatesAliases,
    timeOption,
    timestamp,
  };
};

/** End of Query Preparation Function */

const getAppNotifications = async (
  variables: GetNotificationInput,
  setNotifications: Function,
  setNotificationsState: Function
): Promise<void> => {
  logger.info('getAppNotifications');
  setNotificationsState(DataState.LOADING);
  try {
    const {
      data: { getNotifications },
    } = await makeQuery({
      query: notificationQueries.getNotifications,
      variables,
      clientName: NOTIFICATION_REQUEST,
    });
    const sortedNotifications = [...getNotifications].sort((a: any, b: any) => {
      return b.sentTime.localeCompare(a.sentTime);
    });
    setNotifications(sortedNotifications);
    setNotificationsState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in retrieving inAppNotifications', e as Error);
    setNotificationsState(DataState.ERROR);
  }
};

const prepareUpdateAppNotificationInput = (
  userID: string,
  tmID: string,
  tmAlias: string,
  notificationType: string,
  notificationData?: object,
  emailTemplate?: string,
  locale?: string,
  inAppTitleId?: string,
  inAppMessageId?: string
): SendNotificationInput => {
  return {
    userID: userID,
    receiverId: tmID,
    receiverUsername: tmAlias,
    notificationType: notificationType,
    notificationData: notificationData
      ? JSON.stringify(notificationData)
      : undefined,
    emailTemplate: emailTemplate,
    locale: locale,
    inAppTitleId: inAppTitleId,
    inAppMessageId: inAppMessageId,
  };
};

const updateAppNotification = async (
  variables: UpdateNotificationStatusInput,
  setNotifications: Function,
  setNotificationsState: Function
): Promise<void> => {
  logger.info('updateNotification', variables);
  setNotificationsState(DataState.LOADING);
  try {
    if (!Auth.isImpersonating()) {
      await makeMutation({
        mutation: notificationMutations.updateNotification,
        variables,
        clientName: NOTIFICATION_REQUEST,
      });
    }
    setNotifications('Updated notification with mutation');
    setNotificationsState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in updating notification status', e as Error);
    setNotificationsState(DataState.ERROR);
  }
};

const sendNotification = async (
  variables: SendNotificationInput
): Promise<void> => {
  logger.info('sendNotification', variables);
  return await makeMutation({
    mutation: notificationMutations.sendNotification,
    variables,
  });
};

const getInspireOptions = async (
  dispatch: Function,
  userID?: string
): Promise<void> => {
  logger.info('getInspireOptions', { userID });

  try {
    //UserID is currently only passed for proxy flow, otherwise no userID is passed
    const resp = await makeQuery({
      query: dashboardQueries.getInspireOptions,
      variables: userID ? { userID } : {},
    });

    dispatch({
      type: 'SET_INSPIRE_OPTIONS',
      payload: {
        state: DataState.LOADED,
        data: new InspireOptions(resp.data.getInspireOptions),
      },
    });
  } catch (e) {
    dispatch({
      type: 'SET_INSPIRE_OPTIONS',
      payload: {
        state: DataState.ERROR,
        data: null,
      },
    });
    logger.error('Error in retrieving Inspire Options data', e as Error);
  }
};

const getCoachingNotes = async (
  variables: QueryVariables,
  setCoachingNotes: Function,
  setCoachingNotesState: Function
): Promise<void> => {
  logger.info('getCoachingNotes', variables);
  setCoachingNotesState(DataState.LOADING);
  try {
    const {
      data: { coachingNotes },
    } = await makeQuery({
      query: dashboardQueries.getCoachingNotes,
      variables,
    });

    setCoachingNotes(coachingNotes);
    setCoachingNotesState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving coaching note data',
      e as Error,
      variables
    );
    setCoachingNotesState(DataState.ERROR);
  }
};

const getCoachingComments = async (
  variables: GetCoachingCommentsInput,
  setCoachingComments: Function,
  setCoachingCommentsState: Function
): Promise<void> => {
  logger.info('getCoachingComments');
  setCoachingCommentsState(DataState.LOADING);

  try {
    const {
      data: { getCoachingComments },
    } = await makeQuery({
      query: coachingCommentQueries.getCoachingComments,
      variables,
    });

    setCoachingComments(getCoachingComments);
    setCoachingCommentsState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in retrieving coaching comments', e as Error);
    setCoachingCommentsState(DataState.ERROR);
  }
};

const addCoachingComment = async (
  variables: AddCoachingCommentInput,
  setAddCoachingComment: Function,
  setAddCoachingCommentState: Function
): Promise<void> => {
  logger.info('add coaching comments', variables);
  setAddCoachingCommentState(DataState.LOADING);
  try {
    const {
      data: { addCoachingComment },
    } = await makeMutation({
      mutation: coachingCommentMutations.addCoachingComment,
      variables,
    });
    setAddCoachingComment(addCoachingComment);
    setAddCoachingCommentState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in adding coaching comment', e as Error);
    const errorData = {
      error: (e as Error).message,
    };
    setAddCoachingComment(errorData);
    setAddCoachingCommentState(DataState.ERROR);
  }
};

const writeOutlierException = async (
  variables: WriteOutlierExceptionInput,
  setWriteOutlierException: Function,
  setWriteOutlierExceptionState: Function
): Promise<void> => {
  logger.info('write outlier exception', variables);
  setWriteOutlierExceptionState(DataState.LOADING);
  try {
    // Determine if justification is provided and not empty
    const hasJustification = Boolean(
      variables.justification && variables.justification.trim() !== ''
    );

    // Use the appropriate mutation based on whether justification is provided
    const mutation = getWriteOutlierExceptionMutation(hasJustification);

    // Destructure and conditionally include justification to avoid sending empty string
    const { justification, ...baseVariables } = variables;
    const cleanVariables = hasJustification
      ? { ...baseVariables, justification }
      : baseVariables;

    const {
      data: { writeOutlierException },
    } = await makeMutation({
      mutation,
      variables: cleanVariables,
    });
    setWriteOutlierException(writeOutlierException);
    setWriteOutlierExceptionState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in writing outlier excepion', e as Error);
    const errorData = {
      error: (e as Error).message,
    };
    setWriteOutlierException(errorData);
    setWriteOutlierExceptionState(DataState.ERROR);
  }
};

const updateOutlierException = async (
  variables: UpdateOutlierExceptionInput,
  setUpdateOutlierException: Function,
  setUpdateOutlierExceptionState: Function
): Promise<void> => {
  logger.info('update outlier exception', variables);
  setUpdateOutlierExceptionState(DataState.LOADING);
  try {
    const {
      data: { updateOutlierException },
    } = await makeMutation({
      mutation: outlierMutations.updateOutlierException,
      variables,
    });
    setUpdateOutlierException(updateOutlierException);
    setUpdateOutlierExceptionState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in updating outlier excepion', e as Error);
    const errorData = {
      error: (e as Error).message,
    };
    setUpdateOutlierException(errorData);
    setUpdateOutlierExceptionState(DataState.ERROR);
  }
};

const getWeeklySummaryCoachingStatus = async (
  variables: QueryVariables,
  setWeeklySummaryCoachingStatus: Function,
  setWeeklySummaryCoachingStatusLoadingState: Function
): Promise<void> => {
  logger.info('getWeeklySummaryCoachingStatus', variables);
  setWeeklySummaryCoachingStatusLoadingState(DataState.LOADING);
  try {
    const {
      data: { weeklySummaryCoachingStatus },
    } = await makeQuery({
      query: teamQueries.getWeeklySummaryCoachingStatus,
      variables,
    });
    setWeeklySummaryCoachingStatus(weeklySummaryCoachingStatus);
    setWeeklySummaryCoachingStatusLoadingState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving team weekly summary data',
      e as Error,
      variables
    );
    setWeeklySummaryCoachingStatusLoadingState(DataState.ERROR);
  }
};

const getContactRecommendations = async (
  variables: QueryVariablesRangeRecommendationsGetter,
  setContactRecommendations: Function,
  setContactRecommendationsState: Function
): Promise<void> => {
  logger.info('getContactRecommendations', variables);
  setContactRecommendationsState(DataState.LOADING);
  try {
    const {
      data: { getContactRecommendation },
    } = await makeQuery({
      query: dashboardQueries.getContactRecommendation,
      variables,
    });
    setContactRecommendations(getContactRecommendation);
    setContactRecommendationsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving contact recmmendations data',
      e as Error,
      variables
    );
    setContactRecommendationsState(DataState.ERROR);
  }
};

const getConsolidatedContacts = async (
  variables: QueryVariablesRangeRecommendationsGetter,
  setConsolidatedContacts: Function,
  setConsolidatedContactsState: Function
): Promise<void> => {
  logger.info('getConsolidatedContacts', variables);
  setConsolidatedContactsState(DataState.LOADING);
  try {
    const {
      data: { getConsolidatedContacts },
    } = await makeQuery({
      query: coachingFormQueries.getConsolidatedContacts,
      variables,
    });

    setConsolidatedContacts(getConsolidatedContacts);
    setConsolidatedContactsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving consolidated contacts',
      e as Error,
      variables
    );
    setConsolidatedContactsState(DataState.ERROR);
  }
};

const getFormTemplateData = async (
  variables: GetFormTemplateVariables,
  setForm: Function,
  setFormState: Function
): Promise<void> => {
  logger.info('getFormTemplate', variables);
  setFormState(DataState.LOADING);
  try {
    const {
      data: { getFormTemplates },
    } = await makeQuery({
      query: txMonitoringQueries.getFormTemplates,
      variables,
    });

    setForm(getFormTemplates);
    setFormState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving form template data',
      e as Error,
      variables
    );
    setFormState(DataState.ERROR);
  }
};

const getCCXDetailsData = async (
  variables: Partial<GetCCXDetailsVariables>,
  contactStatusVariables: GetContactStatusVariables,
  setCCXDetailsData: Function,
  setCCXDetailsDataState: Function
): Promise<void> => {
  logger.info('getCCXDetailsData', variables);
  logger.info('getContactStatus', contactStatusVariables);
  setCCXDetailsDataState(DataState.LOADING);
  try {
    const {
      data: { getCCXDetails },
    } = await makeQuery({
      query: getCcxDetails,
      variables,
    });
    const {
      data: { getContactStatusList: getContactStatusListResponse },
    } = await makeQuery({
      query: getContactStatusList,
      variables: {
        users: contactStatusVariables.users,
        startWeek: contactStatusVariables.startWeek,
        endWeek: contactStatusVariables.endWeek,
      },
    });
    const contactStatusMap = new Map<string, string>();
    getContactStatusListResponse.contactStatuses.forEach(
      (item: { commId: string; contactStatus: string }) => {
        contactStatusMap.set(item.commId, item.contactStatus);
      }
    );

    const mergedDetails = getCCXDetails.details.map((detail: any) => {
      const contactStatusName = contactStatusMap.get(detail.contactID);
      return {
        ...detail,
        contactStatus: contactStatusName,
      };
    });
    const mergedCCXDetails = {
      ...getCCXDetails,
      details: mergedDetails,
    };
    setCCXDetailsData(mergedCCXDetails);
    setCCXDetailsDataState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in retrieving ccx details data', {
      error: e as Error,
      variables,
      contactStatusVariables,
    });
    setCCXDetailsDataState(DataState.ERROR);
  }
};

const getCCXForContactData = async (
  variables: Partial<GetCCXForContactVariables>,
  setCCXForContactData: Function,
  setCCXForContactDataState: Function
): Promise<void> => {
  logger.info('getCCXForContactData', variables);
  setCCXForContactDataState(DataState.LOADING);
  try {
    const {
      data: { getCCXForContact },
    } = await makeQuery({
      query: getCCXForContactQuery,
      variables,
    });
    setCCXForContactData(getCCXForContact);
    setCCXForContactDataState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving ccx for contact data',
      e as Error,
      variables
    );
    setCCXForContactDataState(DataState.ERROR);
  }
};

const getProfileData = async (
  variables: QueryVariables,
  setProfileData: Function,
  setProfileDataState: Function
): Promise<void> => {
  logger.info('getProfileData', variables);
  setProfileDataState(DataState.LOADING);
  try {
    const resp = await makeQuery({
      query: dashboardQueries.getProfileData,
      variables,
    });
    const profiles = resp.data.getProfileData;

    setProfileData(new Profile(profiles));
    setProfileDataState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in retrieving profile data', e as Error, variables);
    setProfileDataState(DataState.ERROR);
  }
};

const getTeamProfileData = async (
  variables: QueryVariables | SimpleTeamProfileQueryVariables,
  setTeamProfileData: Function,
  setTeamProfileDataState: Function,
  fetchPolicy?: FetchPolicy
): Promise<void> => {
  logger.info('getTeamProfileData', variables);
  setTeamProfileDataState(DataState.LOADING);
  try {
    const resp = await makeQuery({
      query: dashboardQueries.getTeamProfileData,
      variables,
      ...(fetchPolicy && { fetchPolicy }),
    });

    const teamData = resp.data.getTeamProfileData;
    setTeamProfileData(new TeamProfile(teamData));
    setTeamProfileDataState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving team profile data',
      e as Error,
      variables
    );
    setTeamProfileDataState(DataState.ERROR);
  }
};

const getContacts = async (
  variables: QueryVariablesRange,
  setData: Function,
  setState: Function
): Promise<void> => {
  logger.info('getContacts', variables);
  setState(DataState.LOADING);
  try {
    const {
      data: { contactList },
    } = await makeQuery({
      query: coachingQueries.contactList,
      variables,
    });
    setData(contactList);
    setState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in retrieving contacts data', e as Error, variables);
    setState(DataState.ERROR);
  }
};

const getCustomerCommentsData = async (
  variables: QueryVariablesCustomerComments,
  setCustomerComments: Function,
  setCustomerCommentsState: Function
): Promise<void> => {
  logger.info('getCustomerCommentsData', variables);
  setCustomerCommentsState(DataState.LOADING);
  try {
    const {
      data: { getCustomerCommentsSecure: getCustomerComments },
    } = await makeQuery({
      query: dashboardQueries.getCustomerCommentsSecure,
      variables,
    });

    setCustomerComments(getCustomerComments);
    setCustomerCommentsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving customer comments data',
      e as Error,
      variables
    );
    setCustomerCommentsState(DataState.ERROR);
  }
};

const getSingleCoachingNote = async (
  variables: QueryVariablesContact,
  setSingleCoachingNote: Function,
  setSingleCoachingNoteState: Function
): Promise<void> => {
  logger.info('getSingleCoachingNote', variables);
  setSingleCoachingNoteState(DataState.LOADING);
  try {
    const {
      // eslint-disable-next-line no-shadow
      data: { getSingleCoachingNote },
    } = await makeQuery({
      query: contactQueries.getSingleCoachingNote,
      variables,
    });

    setSingleCoachingNote(getSingleCoachingNote);
    setSingleCoachingNoteState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving coaching history data',
      e as Error,
      variables
    );
    setSingleCoachingNoteState(DataState.ERROR);
  }
};

const getCoachingHistory = async (
  variables: QueryVariables,
  isTM: boolean,

  setCoachingHistory: Function,
  setCoachingHistoryState: Function
): Promise<void> => {
  logger.info('getCoachingHistory', variables);
  setCoachingHistoryState(DataState.LOADING);
  try {
    const { userID } = variables;
    const {
      // eslint-disable-next-line no-shadow
      data: { getCoachingNotes },
    } = await makeQuery({
      query: coachingQueries.getCoachingHistory,
      variables: isTM ? { userID } : variables,
    });

    setCoachingHistory(getCoachingNotes);
    setCoachingHistoryState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving coaching history data',
      e as Error,
      variables
    );
    setCoachingHistoryState(DataState.ERROR);
  }
};

const hideCustomerComment = async (
  hideCustomerCommentInput: HideCustomerCommentInput,
  setHideCustomerComment: Function,
  setHideCustomerCommentState: Function
): Promise<void> => {
  logger.info('hideCustomerComment', hideCustomerCommentInput);
  setHideCustomerCommentState(DataState.LOADING);

  try {
    const {
      // eslint-disable-next-line no-shadow
      data: { hideCustomerComment },
    } = await makeMutation({
      mutation: dashboardMutations.hideCustomerComment,
      variables: hideCustomerCommentInput,
    });

    setHideCustomerComment(hideCustomerComment);
    setHideCustomerCommentState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in hideCustomerComment',
      e as Error,
      hideCustomerCommentInput
    );
    setHideCustomerCommentState(DataState.ERROR);
  }
};

const updateCoachingNote = async (
  coachingFormValues: CoachingFormInputData,
  setSubmitState: Function,
  locale?: string,
  emailFlag?: boolean
): Promise<void> => {
  logger.info('updateCoachingNote', coachingFormValues);
  setSubmitState(DataState.LOADING);
  let newRequest;
  try {
    // eslint-disable-next-line prefer-const
    let { id, ...input } = coachingFormValues;
    input = removeKeys(input, '__typename');
    let newInput = JSON.parse(JSON.stringify(input));

    if (input.coach) newInput = lodash.omit(newInput, ['coach']);
    if (input.reason === enumEmpty)
      newInput = lodash.omit(newInput, ['reason']);
    if (input.coachingSession) {
      if (input.coachingSession.coachingMethod === enumEmpty)
        newInput = lodash.omit(newInput, ['coachingSession.coachingMethod']);
      if (input.coachingSession.coachingTactic === enumEmpty)
        newInput = lodash.omit(newInput, ['coachingSession.coachingTactic']);
      if (input.coachingSession.duration === enumEmpty)
        newInput = lodash.omit(newInput, ['coachingSession.duration']);
      if (input.coachingSession.preparationTime === enumEmpty)
        newInput = lodash.omit(newInput, ['coachingSession.preparationTime']);
    } else {
      // @ts-ignore
      newInput.coachingSession = {
        coachingSummaryNotes: '',
        managerNotes: '',
      };
    }
    // eslint-disable-next-line prefer-const
    let newReviews = JSON.parse(JSON.stringify(input.contactReview));
    input.contactReview.forEach((review, index) => {
      // eslint-disable-next-line no-param-reassign
      let newReview = JSON.parse(JSON.stringify(review));
      if (
        review.marketplaceDomainName ||
        review.marketplaceDomainName === null
      ) {
        newReview = lodash.omit(newReview, ['marketplaceDomainName']);
      }
      newReviews[index] = newReview;
    });
    newInput.contactReview = newReviews;

    // eslint-disable-next-line prefer-const
    let newDetails = JSON.parse(JSON.stringify(input.coachingDetails));
    input.coachingDetails.forEach((detail, index) => {
      // eslint-disable-next-line no-param-reassign
      let newDetail = JSON.parse(JSON.stringify(detail));
      if (detail.relatedMetric === enumEmpty) {
        newDetail = lodash.omit(newDetail, ['relatedMetric']);
      }

      if (detail.relatedContactDomains) {
        newDetail = lodash.omit(newDetail, ['relatedContactDomains']);
      }

      newDetails[index] = newDetail;
    });
    newInput.coachingDetails = newDetails;

    // Fix Apollo - we should do it in a better way
    const mutator = id
      ? coachingMutations.updateDraftCoachingForm
      : coachingMutations.createCoachingForm;

    if (!newInput.otherReason) newInput.otherReason = '';

    // Reassign input
    input = newInput;
    let request;
    if (locale !== undefined) {
      request = { input, id, locale, emailFlag };
    } else {
      request = { input, id };
    }

    newRequest = JSON.parse(JSON.stringify(request));
    if (!id) {
      newRequest = lodash.omit(newRequest, ['id']);
    }
    await makeMutation({
      mutation: mutator,
      variables: newRequest,
    });
    setSubmitState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in updating coaching note data',
      e as Error,
      newRequest
    );
    setSubmitState(DataState.ERROR);
  }
};

const updateContactRecommendation = async (
  contactRecommendationValues: ContactStatusChangeInput,
  setSubmitState: Function
): Promise<void> => {
  logger.info('updateContactRecommendation', contactRecommendationValues);
  setSubmitState(DataState.LOADING);
  const input = contactRecommendationValues;
  try {
    const request = { input };
    await makeMutation({
      mutation: coachingMutations.updateContactStatusNew,
      variables: request,
    });
    setSubmitState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in updating contact recommended data',
      e as Error,
      contactRecommendationValues
    );
    setSubmitState(DataState.ERROR);
  }
};

const removeCoachee = async (
  removeCoacheeInput: RemoveCoacheeInput,
  setRemoveCoacheeState: Function,
  profiles: ExtendedUser[],
  setProfiles: Function
): Promise<void> => {
  logger.info('removeCoachee', removeCoacheeInput);
  setRemoveCoacheeState(DataState.LOADING);

  try {
    await makeMutation({
      mutation: dashboardMutations.deleteCoachee,
      variables: removeCoacheeInput,
    });

    // remove the coachee from in-memory profiles list
    // and update the state
    const filteredProfiles = profiles.filter(
      user => user.userID !== removeCoacheeInput.coacheeID
    );
    setProfiles(filteredProfiles);

    setRemoveCoacheeState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in removing coachee data',
      e as Error,
      removeCoacheeInput
    );
    setRemoveCoacheeState(DataState.ERROR);
  }
};

const removeCoach = async (
  removeCoachInput: RemoveCoachInput,
  setRemoveCoachState: Function,
  profiles: ExtendedUser[],
  setProfiles: Function,
  refetchProfiles?: () => void
): Promise<void> => {
  logger.info('removeCoach', removeCoachInput);
  setRemoveCoachState(DataState.LOADING);

  try {
    await makeMutation({
      mutation: dashboardMutations.deleteCoach,
      variables: removeCoachInput,
    });

    // remove the coach from the in-memory profiles list
    // and update the state
    const filteredProfiles = profiles.filter(
      user => user.userID !== removeCoachInput.coachID
    );
    setProfiles(filteredProfiles);

    setRemoveCoachState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in removing coach', e as Error, removeCoachInput);
    setRemoveCoachState(DataState.ERROR);
  }

  // refresh top level profile
  refetchProfiles?.();
};

const addCoach = async (
  addCoachInput: AddCoachInput,
  setAddState: Function,
  setAddResponse: Function,
  getProfileInput: QueryVariables,
  setProfiles: Function,
  refetchProfiles?: () => void
): Promise<void> => {
  logger.info('addCoach', addCoachInput);
  setAddState(DataState.LOADING);

  let didAddCoachError = false;
  try {
    try {
      const {
        data: { addCoachV2 },
      } = await makeMutation({
        mutation: dashboardMutations.addCoach,
        variables: addCoachInput,
      });

      if (addCoachV2?.messageCode && addCoachV2?.messageCode !== 'SUCCESS') {
        didAddCoachError = true;
      }

      setAddResponse(addCoachV2);
    } catch (e) {
      // TODO: remove this workaround
      if (
        !(e as any).graphQLErrors[0].message.startsWith(
          'Cannot return null for non-nullable type'
        )
      ) {
        logger.error('Error in adding coach', e as Error, addCoachInput);
        throw e;
      }
    }

    // update the list with the new profile
    // TODO: remove call to getProfile, use mutation response instead
    const respProfile = await makeQuery({
      query: dashboardQueries.getProfileData,
      variables: getProfileInput,
    });

    const profile = respProfile.data.getProfileData;
    setProfiles(profile.managers.concat(profile.sharedProfileCoaches));

    setAddState(DataState.LOADED);

    if (!didAddCoachError) {
      // refresh top level profile
      refetchProfiles?.();
    }
  } catch (e) {
    logger.error(
      'Error in updating profile during addCoach',
      e as Error,
      addCoachInput
    );
    setAddState(DataState.ERROR);
  }
};

const removeProxy = async (
  removeProxyInput: RemoveProxyInput,
  setRemoveProxy: Function,
  setRemoveProxyState: Function,
  refetchProfiles?: () => void
): Promise<void> => {
  logger.info('removeProxy', removeProxyInput);
  setRemoveProxyState(DataState.LOADING);

  try {
    const {
      // eslint-disable-next-line no-shadow
      data: { deleteTeamProxyV2 },
    } = await makeMutation({
      mutation: proxyMutations.deleteProxy,
      variables: removeProxyInput,
    });

    setRemoveProxy(deleteTeamProxyV2);
    setRemoveProxyState(DataState.LOADED);

    const { success } = deleteTeamProxyV2;
    if (success) {
      refetchProfiles?.();
    }
  } catch (e) {
    logger.error('Error in removeProxy', e as Error, removeProxyInput);
    setRemoveProxyState(DataState.ERROR);
  }
};

const addProxy = async (
  addProxyInput: AddProxyInput,
  setAddProxy: Function,
  setAddProxyState: Function,
  refetchProfiles?: () => void
): Promise<void> => {
  logger.info('addProxy', addProxyInput);
  setAddProxyState(DataState.LOADING);
  try {
    const {
      // eslint-disable-next-line no-shadow
      data: { addTeamProxyV2 },
    } = await makeMutation({
      mutation: proxyMutations.addProxy,
      variables: addProxyInput,
    });
    setAddProxy(addTeamProxyV2);
    setAddProxyState(DataState.LOADED);

    const { success } = addTeamProxyV2;
    if (success) {
      refetchProfiles?.();
    }
  } catch (e) {
    logger.error('Error in addProxy', e as Error, addProxyInput);
    setAddProxyState(DataState.ERROR);
  }
};

const getProxyNotification = async (
  variables: GetProxyNotificationInput,
  setProxyNotification: Function,
  setProxyNotificationState: Function
): Promise<void> => {
  logger.info('getProxyNotification', variables);
  setProxyNotificationState(DataState.LOADING);
  try {
    const {
      // eslint-disable-next-line no-shadow
      data: { getProxyNotificationV2 },
    } = await makeQuery({
      query: proxyQueries.getProxyNotification,
      variables,
    });
    setProxyNotification(getProxyNotificationV2);
    setProxyNotificationState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving proxy notification data',
      e as Error,
      variables
    );
    setProxyNotificationState(DataState.ERROR);
  }
};

const deleteProxyNotification = async (
  variables: DeleteProxyNotificationInput,
  setDeleteProxyNotification: Function,
  setDeleteProxyNotificationState: Function
): Promise<void> => {
  logger.info('deleteProxyNotification', variables);
  setDeleteProxyNotificationState(DataState.LOADING);
  try {
    const {
      // eslint-disable-next-line no-shadow
      data: { deleteProxyNotificationV2 },
    } = await makeMutation({
      mutation: proxyMutations.deleteProxyNotification,
      variables,
    });
    setDeleteProxyNotification(deleteProxyNotificationV2);
    setDeleteProxyNotificationState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in deleting proxy notification', e as Error, variables);
    setDeleteProxyNotificationState(DataState.ERROR);
  }
};

const getTeamProxy = async (
  variables: GetTeamProxyInput,
  setTeamProxy: Function,
  setTeamProxyState: Function
): Promise<void> => {
  logger.info('getTeamProxy', variables);
  setTeamProxyState(DataState.LOADING);
  try {
    const response = await makeQuery<GetProxy, GetTeamProxyInput>({
      query: proxyQueries.getTeamProxy,
      variables,
    });

    setTeamProxy(response.data);
    setTeamProxyState(DataState.LOADED);
  } catch (e) {
    console.error('Error in retrieving team proxy data', e);
    setTeamProxyState(DataState.ERROR);
  }
};

const getAssociateProfile = async (
  variables: QueryVariablesProfile,
  setAssociateProfile: Function,
  setAssociateProfileState: Function
): Promise<void> => {
  logger.info('getAssociateProfile', variables);
  setAssociateProfileState(DataState.LOADING);
  try {
    const {
      // eslint-disable-next-line no-shadow
      data: { getAssociateProfile },
    } = await makeQuery({
      query: coachingQueries.getAssociateProfile,
      variables,
    });

    setAssociateProfile(getAssociateProfile);
    setAssociateProfileState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving assoicate profile data',
      e as Error,
      variables
    );
    setAssociateProfileState(DataState.ERROR);
  }
};

const getOrgVertical = async (
  variables: QueryVariablesOrgVertical,
  setOrgVertical: Function,
  setOrgVerticalState: Function,
  fetchPolicy?: FetchPolicy
): Promise<void> => {
  logger.info('getOrgVertical', variables);
  setOrgVerticalState(DataState.LOADING);
  try {
    const {
      data: { getHPSUserProfiles },
    } = await makeQuery({
      query: dashboardQueries.getOrgVertical,
      variables,
      fetchPolicy,
    });

    setOrgVertical(getHPSUserProfiles.profiles?.[0]?.USER_VERTICAL ?? null);
    setOrgVerticalState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving org vertical data',
      e as Error,
      variables
    );
    setOrgVerticalState(DataState.ERROR);
  }
};

const getDraftCoachingNote = async (
  variables: QueryVariablesProfile,
  setDraftCoachingNote: Function,
  setDraftCoachingNoteState: Function
): Promise<void> => {
  logger.info('getDraftCoachingNote', variables);
  setDraftCoachingNoteState(DataState.LOADING);
  try {
    const emptyCoachingForm: CoachingFormData = {
      ...EmptyCoachingFormData,
    };
    const { userID } = variables;
    const {
      // eslint-disable-next-line no-shadow
      data: { getCoachingNotes },
    } = await makeQuery({
      query: coachingQueries.getCoachingHistory,
      variables: { userID },
    });

    const filteredNote: CoachingFormData[] = cloneDeep(
      getCoachingNotes.filter(
        (note: { isDelivered: string }) =>
          'isDelivered' in note &&
          note.isDelivered === DeliveredStatusObject.DRAFT
      )
    );

    if (
      filteredNote[0] !== undefined &&
      (filteredNote[0].coachingSession === undefined ||
        filteredNote[0].coachingSession === null)
    ) {
      filteredNote[0].coachingSession = defaultCoachingSessionValues;
    }
    if (filteredNote[0] === undefined) {
      setDraftCoachingNote(emptyCoachingForm);
      setDraftCoachingNoteState(DataState.LOADED);
    } else {
      setDraftCoachingNote(filteredNote[0]);
      setDraftCoachingNoteState(DataState.LOADED);
    }
  } catch (e) {
    logger.error(
      'Error in retrieving draft coaching note data',
      e as Error,
      variables
    );
    setDraftCoachingNoteState(DataState.ERROR);
  }
};

const getContactListeningData = async (
  variables: QueryVariablesContactListeningData,
  setContactListeningData: Function,
  setContactListeningDataState: Function
): Promise<void> => {
  logger.info('getContactListeningData', variables);
  getContactListeningDataCommon(
    contactListeningQueries.getContactListeningData,
    variables,
    setContactListeningData,
    setContactListeningDataState
  );
};

const getContactListeningDataWithChat = async (
  variables: QueryVariablesContactListeningData,
  setContactListeningData: Function,
  setContactListeningDataState: Function
): Promise<void> => {
  logger.info('getContactListeningDataWithChat', variables);
  getContactListeningDataCommon(
    contactListeningQueries.getContactListeningDataWithChat,
    variables,
    setContactListeningData,
    setContactListeningDataState
  );
};

const getContactListeningDataCommon = async (
  query: any,
  variables: QueryVariablesContactListeningData,
  setContactListeningData: Function,
  setContactListeningDataState: Function
): Promise<void> => {
  setContactListeningDataState(DataState.LOADING);
  try {
    const {
      // eslint-disable-next-line no-shadow
      data: { getContactListeningData },
    } = await makeQuery({
      query,
      variables,
    });

    setContactListeningData(getContactListeningData);
    if (getContactListeningData.errors?.length) {
      setContactListeningDataState(DataState.ERROR);
    } else {
      setContactListeningDataState(DataState.LOADED);
    }
  } catch (e) {
    logger.error(
      'Error in retrieving call recording data',
      e as Error,
      variables
    );
    const errorData = {
      errors: [
        {
          message: (e as Error).message,
        },
      ],
    };
    setContactListeningData(errorData);
    setContactListeningDataState(DataState.ERROR);
  }
};

interface QueryVariablesLighthouseMetrics {
  userID: string;
  isTM: boolean;
  vertical: VerticalOption;
  channel: string;
  skill: string;
  segment: string;
  marketplace: string;
  timeOption: TimeOption;
  timestamp: string;
  metrics: LighthouseMetricOption[];
  directReports: string[];
  syncMetrics: boolean;
  isHistorical?: boolean;
}

interface QueryVariablesCustomLighthouseMetrics {
  userID: string;
  isTM: boolean;
  vertical: VerticalOption;
  channel: string;
  skill: string;
  segment: string;
  marketplace: string;
  startTimestamp: string;
  endTimestamp: string;
  metrics: LighthouseMetricOption[];
  directReports: string[];
  syncMetrics: boolean;
  isHistorical?: boolean;
}

interface QueryVariablesTeamLighthouseMetrics {
  userID: string;
  team: string[];
  isTM: boolean[];
  vertical: VerticalOption;
  channel: string;
  skill: string;
  segment: string;
  marketplace: string;
  timeOption: TimeOption;
  timestamp: string;
  syncMetrics: boolean;
}

interface QueryVariablesCustomTeamLighthouseMetrics {
  userID: string;
  team: string[];
  isTM: boolean[];
  vertical: VerticalOption;
  channel: string;
  skill: string;
  segment: string;
  marketplace: string;
  startTimestamp: string;
  endTimestamp: string;
  syncMetrics: boolean;
}

interface QueryVariablesLighthouseFilters {
  userID: string;
  isTM: boolean;
  timeOption: TimeOption;
  timestamp: string;
  team: string[];
  teamMemberIsTM: boolean[];
  syncMetrics: boolean;
  isHistorical?: boolean;
}

interface QueryVariablesLighthouseCustomFilters {
  userID: string;
  isTM: boolean;
  startTimestamp: string;
  endTimestamp: string;
  team: string[];
  teamMemberIsTM: boolean[];
  syncMetrics: boolean;
  isHistorical?: boolean;
}

export interface QueryVariablesContactListeningData {
  userID: string;
  contactStartDate: string;
  contactEndDate: string;
  contactId: string;
  subject: string;
  enableNewAuthFlow: boolean;
  queryType?: ContactListeningQueryType;
}

interface ContactListeningQueryType {
  queryTypeOption?: ContactListeningQueryTypeOption;
  part?: String;
  connectid?: String;
}

interface QueryVariablesTrendsMetrics {
  userID: string;
  team: string[];
  vertical: VerticalOption;
  channel: string;
  skill: string;
  segment: string;
  marketplace: string;
  presetDate: string;
  customDates: string[];
  customMetrics: string[];
  isHistorical?: boolean;
}

interface QueryVariablesTeamDetailsMetrics {
  userID: string;
  team: string[];
  isTM: boolean[];
  vertical: VerticalOption;
  channel: string;
  skill: string;
  segment: string;
  marketplace: string;
  presetDate: string;
  customDates: string[];
  customMetrics: string[];
  syncMetrics: boolean;
}

const parseDirectReports = (
  directReports: any[],
  cscEntityPrefix: string
): string[] => {
  if (!directReports) {
    return [];
  }
  return directReports.map(report => `${cscEntityPrefix}:${report.userID}`);
};

const prepareProxyTeamLighthouseQuery = (
  userID: string,
  cscEntityPrefix: string,
  vertical: VerticalOption,
  channel: string,
  skill: string,
  segment: string,
  marketplace: string,
  timestamp: string,
  timeOption: TimeOption,
  directReports: any[]
): QueryVariablesTeamLighthouseMetrics => {
  const ts =
    timeOption === TimeOption.DAILY
      ? moment(moment.now()).format('YYYY-MM-DD')
      : timestamp;

  return {
    userID: `${cscEntityPrefix}:${userID}`,
    team: parseDirectReports(directReports, cscEntityPrefix),
    isTM: new Array(directReports.length).fill(false),
    vertical,
    channel,
    skill,
    segment,
    marketplace,
    timeOption,
    timestamp: ts,
    syncMetrics: false,
  };
};

const prepareProxyCustomTeamLighthouseQuery = (
  userID: string,
  cscEntityPrefix: string,
  vertical: VerticalOption,
  channel: string,
  skill: string,
  segment: string,
  marketplace: string,
  startTimestamp: string,
  endTimestamp: string,
  directReports: any[]
): QueryVariablesCustomTeamLighthouseMetrics => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
    team: parseDirectReports(directReports, cscEntityPrefix),
    isTM: new Array(directReports.length).fill(false),
    vertical,
    channel,
    skill,
    segment,
    marketplace,
    startTimestamp,
    endTimestamp,
    syncMetrics: false,
  };
};

const prepareTeamLighthouseQuery = (
  userID: string,
  cscEntityPrefix: string,
  vertical: VerticalOption,
  channel: string,
  skill: string,
  segment: string,
  marketplace: string,
  timestamp: string,
  timeOption: TimeOption,
  reportingTeam: ReportingTeamUser[]
): QueryVariablesTeamLighthouseMetrics => {
  if (timeOption === TimeOption.DAILY) {
    return {
      userID: `${cscEntityPrefix}:${userID}`,
      team: parseDirectReports(
        reportingTeam.map(member => member.relatedUser),
        cscEntityPrefix
      ),
      // Currently, GM View is not withint the scope of Team Lighthouse Metrics.
      // We will assume all direct reports are not TMs.
      isTM: reportingTeam.map(member => member.isManager),
      vertical,
      channel,
      skill,
      segment,
      marketplace,
      timeOption,
      timestamp: moment(moment.now()).format('YYYY-MM-DD'),
      syncMetrics: isSyncMetricsEnabled(),
    };
  }

  return {
    userID: `${cscEntityPrefix}:${userID}`,
    team: parseDirectReports(
      reportingTeam.map(member => member.relatedUser),
      cscEntityPrefix
    ),
    // Currently, GM View is not withint the scope of Team Lighthouse Metrics.
    // We will assume all direct reports are not TMs.
    isTM: reportingTeam.map(member => member.isManager),
    vertical,
    channel,
    skill,
    segment,
    marketplace,
    timeOption,
    timestamp,
    syncMetrics: isSyncMetricsEnabled(),
  };
};

const prepareCustomTeamLighthouseQuery = (
  userID: string,
  cscEntityPrefix: string,
  vertical: VerticalOption,
  channel: string,
  skill: string,
  segment: string,
  marketplace: string,
  startTimestamp: string,
  endTimestamp: string,
  reportingTeam: ReportingTeamUser[]
): QueryVariablesCustomTeamLighthouseMetrics => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
    team: parseDirectReports(
      reportingTeam.map(member => member.relatedUser),
      cscEntityPrefix
    ),
    // Currently, GM View is not withint the scope of Team Lighthouse Metrics.
    // We will assume all direct reports are not TMs.
    isTM: reportingTeam.map(member => member.isManager),
    vertical,
    channel,
    skill,
    segment,
    marketplace,
    startTimestamp,
    endTimestamp,
    syncMetrics: isSyncMetricsEnabled(),
  };
};

const prepareLighthouseQuery = (
  userID: string,
  cscEntityPrefix: string,
  isTM: boolean,
  vertical: VerticalOption,
  channel: string,
  skill: string,
  segment: string,
  marketplace: string,
  timestamp: string,
  timeOption: TimeOption,
  metrics: LighthouseMetricOption[],
  directReports: any[],
  syncMetricsDefault?: boolean
): QueryVariablesLighthouseMetrics => {
  const ts =
    timeOption === TimeOption.DAILY
      ? moment(moment.now()).format('YYYY-MM-DD')
      : timestamp;

  return {
    userID: `${cscEntityPrefix}:${userID}`,
    isTM,
    vertical,
    channel,
    skill,
    segment,
    marketplace,
    timeOption,
    timestamp: ts,
    metrics,
    directReports: parseDirectReports(directReports, cscEntityPrefix),
    syncMetrics: isSyncMetricsEnabled(syncMetricsDefault),
    isHistorical: isFeatureEnabled(Feature.HISTORICAL_METRICS),
  };
};

const prepareCustomLighthouseQuery = (
  userID: string,
  cscEntityPrefix: string,
  isTM: boolean,
  vertical: VerticalOption,
  channel: string,
  skill: string,
  segment: string,
  marketplace: string,
  startTimestamp: string,
  endTimestamp: string,
  metrics: LighthouseMetricOption[],
  directReports: any[],
  syncMetricsDefault?: boolean
): QueryVariablesCustomLighthouseMetrics => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
    isTM,
    vertical,
    channel,
    skill,
    segment,
    marketplace,
    startTimestamp,
    endTimestamp,
    metrics,
    directReports: parseDirectReports(directReports, cscEntityPrefix),
    syncMetrics: isSyncMetricsEnabled(syncMetricsDefault),
    isHistorical: isFeatureEnabled(Feature.HISTORICAL_METRICS),
  };
};

const prepareLighthouseFiltersQuery = (
  userID: string,
  cscEntityPrefix: string,
  isTM: boolean,
  timestamp: string,
  timeOption: TimeOption,
  team: string[],
  teamMemberIsTM: boolean[],
  syncMetricsDefault?: boolean
): QueryVariablesLighthouseFilters => {
  if (timeOption === TimeOption.DAILY) {
    return {
      userID: `${cscEntityPrefix}:${userID}`,
      isTM,
      timeOption,
      timestamp: moment(moment.now()).format('YYYY-MM-DD'),
      team: parseDirectReports(team, cscEntityPrefix),
      teamMemberIsTM: teamMemberIsTM,
      syncMetrics: isSyncMetricsEnabled(syncMetricsDefault),
      isHistorical: isFeatureEnabled(Feature.HISTORICAL_METRICS),
    };
  }

  return {
    userID: `${cscEntityPrefix}:${userID}`,
    isTM,
    timeOption,
    timestamp,
    team: parseDirectReports(team, cscEntityPrefix),
    teamMemberIsTM: teamMemberIsTM,
    syncMetrics: isSyncMetricsEnabled(syncMetricsDefault),
    isHistorical: isFeatureEnabled(Feature.HISTORICAL_METRICS),
  };
};

const prepareLighthouseCustomFiltersQuery = (
  userID: string,
  cscEntityPrefix: string,
  isTM: boolean,
  startTimestamp: string,
  endTimestamp: string,
  team: string[],
  teamMemberIsTM: boolean[],
  syncMetricsDefault?: boolean
): QueryVariablesLighthouseCustomFilters => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
    isTM,
    startTimestamp,
    endTimestamp,
    team: parseDirectReports(team, cscEntityPrefix),
    teamMemberIsTM: teamMemberIsTM,
    syncMetrics: isSyncMetricsEnabled(syncMetricsDefault),
    isHistorical: isFeatureEnabled(Feature.HISTORICAL_METRICS),
  };
};

const prepareContactListeningDataQuery = (
  userID: string,
  contactId: string,
  contactStartDate: string,
  contactEndDate: string,
  subject: string,
  enableNewAuthFlow: boolean,
  queryType: ContactListeningQueryType
): QueryVariablesContactListeningData => {
  return {
    userID: userID,
    contactId: contactId,
    contactStartDate: contactStartDate,
    contactEndDate: contactEndDate,
    subject: subject,
    enableNewAuthFlow: enableNewAuthFlow ?? false,
    queryType: queryType,
  };
};

const prepareTrendsMetricsQuery = (
  userID: string,
  cscEntityPrefix: string,
  team: string[],
  vertical: VerticalOption,
  channel: string,
  skill: string,
  segment: string,
  marketplace: string,
  presetDate: string,
  customDates?: string[],
  customMetrics?: string[]
): QueryVariablesTrendsMetrics => {
  if (customDates && customMetrics) {
    return {
      userID: `${cscEntityPrefix}:${userID}`,
      team: parseDirectReports(team, cscEntityPrefix),
      vertical,
      channel,
      skill,
      segment,
      marketplace,
      presetDate,
      customDates,
      customMetrics,
      isHistorical: isFeatureEnabled(Feature.HISTORICAL_METRICS),
    };
  } else {
    return {
      userID: `${cscEntityPrefix}:${userID}`,
      team: parseDirectReports(team, cscEntityPrefix),
      vertical,
      channel,
      skill,
      segment,
      marketplace,
      presetDate,
      customDates: [],
      customMetrics: [],
      isHistorical: isFeatureEnabled(Feature.HISTORICAL_METRICS),
    };
  }
};

const prepareTeamDetailsMetricsQuery = (
  userID: string,
  cscEntityPrefix: string,
  reportingTeam: ReportingTeamUser[],
  vertical: VerticalOption,
  channel: string,
  skill: string,
  segment: string,
  marketplace: string,
  presetDate: string,
  customDates?: string[],
  customMetrics?: string[],
  syncMetrics?: boolean
): QueryVariablesTeamDetailsMetrics => {
  if (customDates && customMetrics) {
    return {
      userID: `${cscEntityPrefix}:${userID}`,
      team: parseDirectReports(
        reportingTeam.map(member => member.relatedUser),
        cscEntityPrefix
      ),
      isTM: reportingTeam.map(member => member.isManager),
      vertical,
      channel,
      skill,
      segment,
      marketplace,
      presetDate,
      customDates,
      customMetrics,
      syncMetrics: syncMetrics ?? false,
    };
  } else
    return {
      userID: `${cscEntityPrefix}:${userID}`,
      team: parseDirectReports(
        reportingTeam.map(member => member.relatedUser),
        cscEntityPrefix
      ),
      isTM: reportingTeam.map(member => member.isManager),
      vertical,
      channel,
      skill,
      segment,
      marketplace,
      presetDate,
      customDates: [],
      customMetrics: [],
      syncMetrics: syncMetrics ?? false,
    };
};

const prepareTeamProfileDataQuery = (
  userID: string,
  cscEntityPrefix: string
): TeamProfileDataQueryInput => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
  };
};

const prepareCreateGoalInput = (
  userID: string,
  cscEntityPrefix: string,
  userName: string,
  vertical: string,
  goalType: GoalLevel,
  goalDescription: string,
  metric: GoalMetricOption,
  targetValue: string,
  periodType: GoalPeriodType,
  periodValue: string,
  createdBy: string
): CreateGoalInput => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
    input: {
      userID: `${cscEntityPrefix}:${userID}`,
      userName: userName,
      vertical: vertical,
      level: goalType,
      description: goalDescription,
      metric: metric,
      targetValue: targetValue,
      periodType: periodType,
      periodValue: periodValue,
      createdBy: createdBy,
    },
  };
};

const prepareGoalJustificationInput = (
  authorUserName: string,
  goalID: string,
  justification: string,
  userID: string
): GoalJustificationInput => {
  return {
    input: {
      authorUserName: authorUserName,
      userID: userID,
      goalID: goalID,
      justification: justification,
    },
    userID: userID,
  };
};

const prepareQueryForGetGoalComments = (
  userID: string,
  goalID: string
): GetGoalCommentsInput => {
  return {
    userID: userID,
    goalID: goalID,
  };
};

export interface CommentInput {
  userID: string;
  goalID: string;
  authorUserName: string;
  comment: string;
}

export interface GoalCommentInput {
  userID: string;
  input: CommentInput;
}

const prepareAddGoalCommentInput = (
  userID: string,
  goalID: string,
  authorUserName: string,
  comment: string
): GoalCommentInput => {
  return {
    userID: userID,
    input: {
      userID: userID,
      goalID: goalID,
      authorUserName: authorUserName,
      comment: comment,
    },
  };
};

const getTeamLighthouseMetrics = async (
  variables: QueryVariablesTeamLighthouseMetrics,
  setTeamLighthouseMetrics: Function,
  setTeamLighthouseMetricsState: Function
): Promise<void> => {
  logger.info('getTeamLighthouseMetrics', variables);
  setTeamLighthouseMetricsState(DataState.LOADING);
  try {
    const {
      data: { getTeamLighthouseMetrics },
    } = await makeQuery({
      query: teamQueries.getTeamLighthouseMetrics,
      variables,
    });

    const filteredMetrics = getTeamLighthouseMetrics.map(
      filterLighthouseMetricCCXNeg
    );
    setTeamLighthouseMetrics(filteredMetrics);
    setTeamLighthouseMetricsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving Lighthouse Metric Team data',
      e as Error,
      variables
    );
    setTeamLighthouseMetricsState(DataState.ERROR);
  }
};

const getCustomTeamLighthouseMetrics = async (
  variables: QueryVariablesCustomTeamLighthouseMetrics,
  setCustomTeamLighthouseMetrics: Function,
  setCustomTeamLighthouseMetricsState: Function
): Promise<void> => {
  logger.info('getCustomTeamLighthouseMetrics', variables);
  setCustomTeamLighthouseMetricsState(DataState.LOADING);
  try {
    const {
      data: { getCustomTeamLighthouseMetrics },
    } = await makeQuery({
      query: teamQueries.getCustomTeamLighthouseMetrics,
      variables,
    });
    const filteredMetrics = getCustomTeamLighthouseMetrics.map(
      filterLighthouseMetricCCXNeg
    );
    setCustomTeamLighthouseMetrics(filteredMetrics);
    setCustomTeamLighthouseMetricsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving Custom Lighthouse Metric Team data',
      e as Error,
      variables
    );
    setCustomTeamLighthouseMetricsState(DataState.ERROR);
  }
};

const filterMetricType = (originalLighthouseMetric: LighthouseMetrics) => {
  const lighthouseMetric = cloneDeep(originalLighthouseMetric);
  lighthouseMetric.metricTypes.sort();

  return lighthouseMetric;
};

const filterLighthouseMetricCCXNeg = (
  originalLighthouseMetric: LighthouseMetrics
) => {
  const lighthouseMetric = cloneDeep(originalLighthouseMetric);
  const shouldShowTwoBox = isFeatureEnabled(Feature.CCX_BOTTOM_TWO_BOX);
  if (shouldShowTwoBox) {
    lighthouseMetric.CCX_NEG_ONEBOX = null;
  } else {
    lighthouseMetric.CCX_NEG = null;
  }

  lighthouseMetric.metricTypes = filterCCXNeg(lighthouseMetric.metricTypes);
  return lighthouseMetric;
};

const getLighthouseMetrics = async (
  variables: QueryVariablesLighthouseMetrics,
  setLighthouseMetrics: Function,
  setLighthouseMetricsState: Function
): Promise<void> => {
  logger.info('getLighthouseMetrics', variables);

  setLighthouseMetricsState(DataState.LOADING);
  try {
    const {
      // eslint-disable-next-line no-shadow
      data: { getLighthouseMetrics },
    } = await makeQuery({
      query: dashboardQueries.getLighthouseMetrics,
      variables,
    });

    let filteredMetrics = filterMetricType(getLighthouseMetrics);
    filteredMetrics = filterLighthouseMetricCCXNeg(getLighthouseMetrics);
    setLighthouseMetrics(filteredMetrics);
    setLighthouseMetricsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving lighthouse metrics data',
      e as Error,
      variables
    );
    setLighthouseMetricsState(DataState.ERROR);
  }
};

const getCustomLighthouseMetrics = async (
  variables: QueryVariablesCustomLighthouseMetrics,
  setCustomLighthouseMetrics: Function,
  setCustomLighthouseMetricsState: Function
): Promise<void> => {
  logger.info('getCustomLighthouseMetrics', variables);

  setCustomLighthouseMetricsState(DataState.LOADING);
  try {
    const {
      // eslint-disable-next-line no-shadow
      data: { getCustomLighthouseMetrics },
    } = await makeQuery({
      query: teamQueries.getCustomLighthouseMetrics,
      variables,
    });

    let filteredMetrics = filterMetricType(getCustomLighthouseMetrics);
    filteredMetrics = filterLighthouseMetricCCXNeg(getCustomLighthouseMetrics);
    setCustomLighthouseMetrics(filteredMetrics);
    setCustomLighthouseMetrics(getCustomLighthouseMetrics);
    setCustomLighthouseMetricsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving lighthouse metrics data',
      e as Error,
      variables
    );
    setCustomLighthouseMetricsState(DataState.ERROR);
  }
};

const filterSort = (originalFilter: LighthouseFilters) => {
  const filter = cloneDeep(originalFilter);
  filter.channels.sort();
  filter.skills.sort();
  filter.verticals.sort();
  filter.segments.sort();
  if (filter.marketplaces) {
    filter.marketplaces.sort();
  }
  return filter;
};

const getLighthouseFilters = async (
  variables: QueryVariablesLighthouseFilters,
  setLighthouseFilters: Function,
  setLighthouseFiltersState: Function
): Promise<void> => {
  logger.info('getLighthouseFilters', variables);
  setLighthouseFiltersState(DataState.LOADING);
  try {
    const {
      // eslint-disable-next-line no-shadow
      data: { getFilterOptions: getLighthouseFilters },
    } = await makeQuery({
      query: dashboardQueries.getFilterOptions,
      variables,
    });

    const sortedFilters = filterSort(getLighthouseFilters);
    setLighthouseFilters(sortedFilters);
    setLighthouseFiltersState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving lighthouse filters data',
      e as Error,
      variables
    );
    setLighthouseFiltersState(DataState.ERROR);
  }
};

const getLighthouseCustomFilters = async (
  variables: QueryVariablesLighthouseCustomFilters,
  setLighthouseCustomFilters: Function,
  setLighthouseCustomFiltersState: Function
): Promise<void> => {
  logger.info('getLighthouseCustomFilters', variables);
  setLighthouseCustomFiltersState(DataState.LOADING);
  try {
    const {
      // eslint-disable-next-line no-shadow
      data: { getCustomFilterOptions: getLighthouseCustomFilters },
    } = await makeQuery({
      query: teamQueries.getCustomFilterOptions,
      variables,
    });

    const sortedFilters = filterSort(getLighthouseCustomFilters);
    setLighthouseCustomFilters(sortedFilters);
    setLighthouseCustomFiltersState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving lighthouse filters data',
      e as Error,
      variables
    );
    setLighthouseCustomFiltersState(DataState.ERROR);
  }
};

interface QueryVariablesLightDefaultVertical {
  userID: string;
}

const prepareLighthouseDefaultVerticalQuery = (
  userID: string,
  cscEntityPrefix: string
): QueryVariablesLightDefaultVertical => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
  };
};

const getDefaultVertical = async (
  variables: QueryVariablesLightDefaultVertical,
  setDefaultVertical: Function,
  setDefaultVerticalState: Function
): Promise<void> => {
  logger.info('getDefaultVertical', variables);
  setDefaultVerticalState(DataState.LOADING);
  try {
    const {
      // eslint-disable-next-line no-shadow
      data: { getDefaultVertical },
    } = await makeQuery({
      query: dashboardQueries.getDefaultVertical,
      variables,
    });
    setDefaultVertical(getDefaultVertical ?? VerticalOption.Total);
    setDefaultVerticalState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving default vertical data',
      e as Error,
      variables
    );
    setDefaultVerticalState(DataState.ERROR);
  }
};

interface QueryVariablesHmdDetail {
  userID: string;
  isTM: boolean;
  directReports: string[];
  vertical: VerticalOption;
  skill: string;
  channel: string;
  segment: string;
  marketplace: string;
  timeOption: TimeOption;
  timestamp: string;
  syncMetrics: boolean;
}

const prepareHmdDetailsQuery = (
  userID: string,
  isTM: boolean,
  directReports: any[],
  vertical: VerticalOption,
  skill: string,
  channel: string,
  segment: string,
  marketplace: string,
  timeOption: TimeOption,
  timestamp: string,
  cscEntityPrefix: string
): QueryVariablesHmdDetail => {
  if (timeOption === TimeOption.DAILY) {
    return {
      userID: `${cscEntityPrefix}:${userID}`,
      isTM,
      directReports: parseDirectReports(directReports, cscEntityPrefix),
      vertical,
      skill,
      channel,
      segment,
      marketplace,
      timeOption,
      timestamp: moment(moment.now()).format('YYYY-MM-DD'),
      syncMetrics: isSyncMetricsEnabled(),
    };
  }

  return {
    userID: `${cscEntityPrefix}:${userID}`,
    isTM,
    directReports: parseDirectReports(directReports, cscEntityPrefix),
    vertical,
    skill,
    channel,
    segment,
    marketplace,
    timeOption,
    timestamp,
    syncMetrics: isSyncMetricsEnabled(),
  };
};

const getHMDDetails = async (
  variables: QueryVariablesHmdDetail,
  setHmdDetails: Function,
  setHmdDetailsState: Function
): Promise<void> => {
  logger.info('getHmdDetails', variables);
  setHmdDetailsState(DataState.LOADING);
  try {
    const {
      // eslint-disable-next-line no-shadow
      data: { getHMDDetails },
    } = await makeQuery({
      query: dashboardQueries.getHMDDetails,
      variables,
    });

    setHmdDetails(getHMDDetails);
    setHmdDetailsState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in retrieving HmdDetails data', e as Error, variables);
    setHmdDetailsState(DataState.ERROR);
  }
};

interface GetRealtimeHmdDetailsQueryVariables {
  userID: string;
  isTM: boolean;
  directReports: string[];
  startDate: string;
  endDate: string;
}

const prepareRealtimeHmdDetailsQuery = (
  userID: string,
  cscEntityPrefix: string,
  isTM: boolean,
  directReports: any[],
  start: string,
  end: string
): GetRealtimeHmdDetailsQueryVariables => {
  return {
    userID: `${cscEntityPrefix}:${userID}`,
    isTM,
    directReports: parseDirectReports(directReports, cscEntityPrefix),
    startDate: start,
    endDate: end,
  };
};

const getRealtimeHmdDetails = async (
  variables: GetRealtimeHmdDetailsQueryVariables,
  setRealTimeHmdDetails: Function,
  setRealTimeHmdDetailsState: Function
): Promise<void> => {
  logger.info('getRealtimeHmdDetails', variables);
  setRealTimeHmdDetailsState(DataState.LOADING);
  try {
    const {
      // eslint-disable-next-line no-shadow
      data: { getRealtimeHmdDetails },
    } = await makeQuery({
      query: dashboardQueries.getRealtimeHmdDetails,
      variables,
    });

    setRealTimeHmdDetails(getRealtimeHmdDetails);
    setRealTimeHmdDetailsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving real time HmdDetails data',
      e as Error,
      variables
    );
    setRealTimeHmdDetailsState(DataState.ERROR);
  }
};

const prepareContactStatusQuery = (
  userID: string,
  cscEntityPrefix: string,
  isTM: boolean,
  directReports: DirectReport[],
  startWeek: string,
  endWeek: string
): GetContactStatusVariables => {
  const users = isTM
    ? parseDirectReports(directReports, cscEntityPrefix)
    : [`${cscEntityPrefix}:${userID}`];

  return {
    users,
    startWeek,
    endWeek,
  };
};

const getContactStatus = async (
  contactStatusVariables: GetContactStatusVariables,
  setContactStatusDetails: Function,
  setContactStatusDetailsState: Function
): Promise<void> => {
  logger.info('getContactStatus', contactStatusVariables);
  setContactStatusDetailsState(DataState.LOADING);
  try {
    const {
      data: { getContactStatusList: getContactStatusListResponse },
    } = await makeQuery({
      query: getContactStatusList,
      variables: contactStatusVariables,
    });
    setContactStatusDetails(getContactStatusListResponse);
    setContactStatusDetailsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving contact status data',
      e as Error,
      contactStatusVariables
    );
    setContactStatusDetails({ contactStatuses: [] });
    setContactStatusDetailsState(DataState.ERROR);
  }
};

const isSyncMetricsEnabled = (syncMetricsDefault?: boolean): boolean => {
  if (syncMetricsDefault !== undefined) {
    return syncMetricsDefault;
  }

  return false;
};

const getFeedbackMessages = async (
  variables: GetFeedbackMessagesInput,
  setFeedbackMsgs: Function,
  setFeedbackMsgsState: Function
): Promise<void> => {
  logger.info('getFeedbackMessages', variables);
  setFeedbackMsgsState(DataState.LOADING);
  try {
    const {
      // eslint-disable-next-line no-shadow
      data: { getFeedbackMessages },
    } = await makeQuery({
      query: dashboardQueries.getFeedbackMessages,
      variables,
    });

    setFeedbackMsgs(getFeedbackMessages);
    setFeedbackMsgsState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in retrieving QCS performance messages', e as Error);
    setFeedbackMsgsState(DataState.ERROR);
  }
};

const updateFormReview = async (
  formReviewValues: UpdateFormReviewInput,
  setFormStatusCode: Function,
  setFormStatusCodeState: Function,
  setExceptionMessage: Function
): Promise<void> => {
  logger.info('UpdateFormReview Operation:', formReviewValues);
  setFormStatusCodeState(DataState.LOADING);
  const input = removeKeys(formReviewValues, '__typename');

  try {
    // Remove justification field from contactDetailsInput as its not needed
    if (input.contactDetailsInput?.justification !== undefined) {
      delete input.contactDetailsInput.justification;
    }

    const request = { input };
    const {
      data: { updateFormReview },
    } = await makeMutation({
      mutation: FormLibraryMutation.updateFormReview,
      variables: request,
    });

    setFormStatusCode(updateFormReview);
    setFormStatusCodeState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in updating form review', e as Error, formReviewValues);
    setFormStatusCodeState(DataState.ERROR);
    setExceptionMessage((e as Error).message);
  }
};

const getCoachingsAndContactReviews = async (
  variables: GetCoachingsAndContactReviewsInput,
  setCoachingsAndContactReviews: Function,
  setCoachingsAndContactReviewsState: Function,
  fetchPolicy?: FetchPolicy
): Promise<void> => {
  logger.info('getCoachingsAndContactReviews', variables);
  setCoachingsAndContactReviewsState(DataState.LOADING);
  try {
    const {
      data: { getCoachingsAndContactReviews },
    } = await makeQuery({
      query: getCoachingsAndContactReviewsQuery,
      variables,
      fetchPolicy,
    });
    setCoachingsAndContactReviews(getCoachingsAndContactReviews);
    setCoachingsAndContactReviewsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving coachings and contact reviews',
      e as Error,
      variables
    );
    setCoachingsAndContactReviewsState(DataState.ERROR);
  }
};

const getCoachingStats = async (
  variables: GetCoachingStatsAppSyncRequest,
  setCoachingStats: Function,
  setCoachingStatsState: Function
): Promise<void> => {
  logger.info('getCoachingStats', variables);
  setCoachingStatsState(DataState.LOADING);
  try {
    const {
      data: { getCoachingStats },
    } = await makeQuery<
      GetCoachingStatsAppSyncResponse,
      GetCoachingStatsAppSyncRequest
    >({
      query: getCoachingStatsQuery,
      variables,
    });

    setCoachingStats(getCoachingStats);
    setCoachingStatsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving stats details data',
      e as Error,
      variables
    );
    setCoachingStatsState(DataState.ERROR);
  }
};

const getBatchCoachingStats = async (
  variables: GetBatchCoachingStatsRequest,
  setBatchCoachingStats: Function,
  setBatchCoachingStatsState: Function,
  fetchPolicy?: FetchPolicy
): Promise<void> => {
  logger.info('getBatchCoachingStats', variables);
  setBatchCoachingStatsState(DataState.LOADING);
  try {
    const {
      data: { getBatchCoachingStats },
    } = await makeQuery<
      GetBatchCoachingStatsAppSyncResponse,
      GetBatchCoachingStatsAppSyncRequest
    >({
      query: getBatchCoachingStatsQuery,
      variables,
      fetchPolicy,
    });
    setBatchCoachingStats(getBatchCoachingStats);
    setBatchCoachingStatsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving batch coaching stats data',
      e as Error,
      variables
    );
    setBatchCoachingStatsState(DataState.ERROR);
  }
};

const filterTrendAndDetailsCCXNeg = (
  trendAndDetailsMetrics: TrendAndDetailsMetrics
) => {
  const filteredTrendsAndDetailsMetrics: TrendAndDetailsMetrics = {
    ...trendAndDetailsMetrics,
    metricDatas: {
      trend: filterCCXNeg(
        trendAndDetailsMetrics.metricDatas?.trend ?? [],
        trend => trend.name
      ),
      baseMetric: filterCCXNeg(
        trendAndDetailsMetrics.metricDatas?.baseMetric ?? [],
        baseMetric => baseMetric.name
      ),
    },
    metricTypes: filterCCXNeg(trendAndDetailsMetrics.metricTypes),
  };
  return filteredTrendsAndDetailsMetrics;
};

interface TrendAndDetailsMetricsResponse {
  getTrendsMetrics: TrendAndDetailsMetrics;
}
const getTrendsMetrics = async (
  variables: QueryVariablesTrendsMetrics,
  setTrendsMetrics: Function,
  setTrendsMetricsState: Function
): Promise<void> => {
  logger.info('getTrendsMetrics', variables);
  setTrendsMetricsState(DataState.LOADING);
  try {
    const {
      data: { getTrendsMetrics },
    } = await makeQuery<
      TrendAndDetailsMetricsResponse,
      QueryVariablesTrendsMetrics
    >({
      query: getTrendsMetricsQuery,
      variables,
    });
    const filteredTrendsMetrics = filterTrendAndDetailsCCXNeg(getTrendsMetrics);
    setTrendsMetrics(filteredTrendsMetrics);
    setTrendsMetricsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving trend metrics data',
      e as Error,
      variables
    );
    setTrendsMetricsState(DataState.ERROR);
  }
};

interface GetTeamDetailsMetricsResponse {
  getTeamDetailsMetrics: TrendAndDetailsMetrics[];
}

const getTeamDetailsMetrics = async (
  variables: QueryVariablesTeamDetailsMetrics,
  setTeamDetailsMetrics: Function,
  setTeamDetailsMetricsState: Function
): Promise<void> => {
  logger.info('getTeamDetailsMetrics', variables);
  setTeamDetailsMetricsState(DataState.LOADING);
  try {
    const {
      data: { getTeamDetailsMetrics },
    } = await makeQuery<
      GetTeamDetailsMetricsResponse,
      QueryVariablesTeamDetailsMetrics
    >({
      query: getTeamDetailsMetricsQuery,
      variables,
    });
    // note - no need to filter CCX B1B / B2B here as desired metrics are included in request
    setTeamDetailsMetrics(getTeamDetailsMetrics);
    setTeamDetailsMetricsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving team details metrics data',
      e as Error,
      variables
    );
    setTeamDetailsMetricsState(DataState.ERROR);
  }
};

const filterBQCCX = (
  metrics: BottomQuartileMetric[],
  shouldShowTwoBox: boolean
) => {
  return metrics.filter(metric => {
    if (
      metric.metricName === MetricsOption.CCX_NEG_ONEBOX &&
      shouldShowTwoBox
    ) {
      return false;
    }
    if (metric.metricName === MetricsOption.CCX_NEG && !shouldShowTwoBox) {
      return false;
    }

    return true;
  });
};

const getAssociateInsights = async (
  variables: GetAssociateInsightsInput,
  setAssociateInsights: Function,
  setAssociateInsightsState: Function
): Promise<void> => {
  logger.info('getAssociateInsights', variables);
  setAssociateInsightsState(DataState.LOADING);
  try {
    const {
      data: { getAssociateInsights },
    }: { data: { getAssociateInsights: AssociateInsight[] } } = await makeQuery(
      {
        query: getAssociateInsightsQuery,
        variables,
      }
    );

    // filter out BQ CCX bottom one box if feature flag enabled
    const shouldShowTwoBox = isFeatureEnabled(Feature.CCX_BOTTOM_TWO_BOX);
    const filteredAssociateInsights = getAssociateInsights.map(insight => ({
      ...insight,
      bottomQuartileMetrics: filterBQCCX(
        insight.bottomQuartileMetrics,
        shouldShowTwoBox
      ),
    }));

    setAssociateInsights(filteredAssociateInsights);
    setAssociateInsightsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving associate insights details data',
      e as Error,
      variables
    );
    setAssociateInsightsState(DataState.ERROR);
  }
};

const getTrainingStats = async (
  variables: GetTrainingStatsInput,
  setTrainingStats: Function,
  setTrainingStatsState: Function
): Promise<void> => {
  logger.info('getTrainingStats', variables);
  setTrainingStatsState(DataState.LOADING);
  try {
    const {
      data: { getTrainingStats },
    }: { data: { getTrainingStats: TrainingStatsResult } } = await makeQuery({
      query: trainingQueries.getTrainingStatsQuery,
      variables,
    });

    setTrainingStats(getTrainingStats);
    setTrainingStatsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving training stats data',
      e as Error,
      variables
    );
    setTrainingStatsState(DataState.ERROR);
  }
};

const createGoal = async (
  variables: CreateGoalInput,
  setCreateGoal: Function,
  setCreateGoalState: Function
): Promise<void> => {
  logger.info('createGoal', variables);
  setCreateGoalState(DataState.LOADING);
  try {
    const {
      data: { createGoal },
    } = await makeMutation({
      mutation: goalMutations.createGoal,
      variables,
    });
    setCreateGoal(createGoal);
    setCreateGoalState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in createGoal', e as Error);
    const errorData = {
      error: (e as Error).message,
    };
    setCreateGoal(errorData);
    setCreateGoalState(DataState.ERROR);
  }
};

const getTeamLevelGoals = async (
  variables: GetTeamLevelGoalsInput,
  setTeamLevelGoals: Function,
  setTeamLevelGoalsState: Function
): Promise<void> => {
  logger.info('getTeamLevelGoals', variables);
  setTeamLevelGoalsState(DataState.LOADING);
  try {
    const {
      data: { getTeamLevelGoals },
    } = await makeQuery({
      query: goalQueries.getTeamLevelGoals,
      variables,
    });
    setTeamLevelGoals(getTeamLevelGoals);
    setTeamLevelGoalsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving TeamLevelGoals data',
      e as Error,
      variables
    );
    setTeamLevelGoalsState(DataState.ERROR);
  }
};

const getAssociateLevelGoals = async (
  variables: GetAssociateLevelGoalsInput,
  setAssociateLevelGoals: Function,
  setAssociateLevelGoalsState: Function
): Promise<void> => {
  logger.info('getAssociateLevelGoals', variables);
  setAssociateLevelGoalsState(DataState.LOADING);
  try {
    const {
      data: { getAssociateLevelGoals },
    } = await makeQuery({
      query: goalQueries.getAssociateLevelGoals,
      variables,
    });
    setAssociateLevelGoals(getAssociateLevelGoals);
    setAssociateLevelGoalsState(DataState.LOADED);
  } catch (e) {
    logger.error(
      'Error in retrieving AssociateLevelGoals data',
      e as Error,
      variables
    );
    setAssociateLevelGoalsState(DataState.ERROR);
  }
};

const addGoalJustification = async (
  variables: GoalJustificationInput,
  setAddGoalJustification: Function,
  setAddGoalJustificationState: Function
): Promise<void> => {
  logger.info('addGoalJustification', variables);
  setAddGoalJustificationState(DataState.LOADING);
  try {
    const {
      data: { addGoalJustification },
    } = await makeMutation({
      mutation: goalMutations.addGoalJustification,
      variables,
    });
    setAddGoalJustification(addGoalJustification);
    setAddGoalJustificationState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in addGoalJustification', e as Error);
    const errorData = {
      error: (e as Error).message,
    };
    setAddGoalJustification(errorData);
    setAddGoalJustificationState(DataState.ERROR);
  }
};

const getGoalComments = async (
  variables: GetGoalCommentsInput,
  setGoalComments: Function,
  setGoalCommentsState: Function
): Promise<void> => {
  logger.info('getGoalComments');
  setGoalCommentsState(DataState.LOADING);
  try {
    const {
      data: { getGoalComments },
    } = await makeQuery({
      query: goalCommentsQueries.getGoalComments,
      variables,
    });
    setGoalComments(getGoalComments);
    setGoalCommentsState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in retrieving goal coments', e as Error);
    setGoalCommentsState(DataState.ERROR);
  }
};

const addGoalComment = async (
  variables: GoalCommentInput,
  setAddGoalComment: Function,
  setAddGoalCommentState: Function
): Promise<void> => {
  logger.info('addGoalComment', variables);
  setAddGoalCommentState(DataState.LOADING);
  try {
    const {
      data: { addGoalComment },
    } = await makeMutation({
      mutation: goalCommentsMutations.addGoalComment,
      variables,
    });
    setAddGoalComment(addGoalComment);
    setAddGoalCommentState(DataState.LOADED);
  } catch (e) {
    logger.error('Error in addGoalComment', e as Error);
    const errorData = {
      error: (e as Error).message,
    };
    setAddGoalComment(errorData);
    setAddGoalCommentState(DataState.ERROR);
  }
};

const getCSAWeeklySummaries = async (
  variables: summariesQueries.GetCSAWeeklySummariesVariables,
  setWeeklySummaries: (
    response: summariesQueries.GetCSAWeeklySummariesResponse
  ) => void,
  setWeeklySummariesState: Function,
  fetchPolicy: FetchPolicy = 'cache-first'
): Promise<void> => {
  logger.info('getCSAWeeklySummaries');
  setWeeklySummariesState(DataState.LOADING);
  try {
    const {
      data: { getCSAWeeklySummaries },
    } = await makeQuery<
      summariesQueries.GetWeeklySummariesAppsyncResponse,
      summariesQueries.GetCSAWeeklySummariesVariables
    >({
      query: summariesQueries.getCSAWeeklySummaries,
      variables,
      fetchPolicy,
    });

    if (getCSAWeeklySummaries.error) {
      setWeeklySummariesState(DataState.ERROR);
    } else {
      setWeeklySummaries(getCSAWeeklySummaries);
      setWeeklySummariesState(DataState.LOADED);
    }
  } catch (e) {
    logger.error('Error in retrieving CSA summaries', e as Error);
    setWeeklySummariesState(DataState.ERROR);
  }
};

export interface SubmitCSAFeedbackVariables {
  alias: string;
  date: string;
  isSummaryGood: boolean;
  summaryUUID: string;
}

export interface SubmitManagerFeedbackVariables {
  alias: string;
  csaAlias: string;
  date: string;
  feedback: ManagerFeedbackInput;
  summaryUUID: string;
}

interface SubmitManagerFeedbackResponse {
  data: {
    submitCSASummariesManagerFeedback: {
      success: boolean;
      message: string;
      error: string;
    };
  };
}

const submitCSASummariesManagerFeedback = async (
  variables: SubmitManagerFeedbackVariables,
  setSubmitManagerFeedback: Function,
  setSubmitManagerFeedbackState: Function
): Promise<void> => {
  logger.info('submitManagerFeedback', variables);
  setSubmitManagerFeedbackState(DataState.LOADING);
  try {
    const {
      data: { submitCSASummariesManagerFeedback },
    }: SubmitManagerFeedbackResponse = await makeMutation({
      mutation: summariesMutations.submitCSASummariesManagerFeedback,
      variables,
    });
    if (submitCSASummariesManagerFeedback.error) {
      setSubmitManagerFeedbackState(DataState.ERROR);
    } else {
      setSubmitManagerFeedback(submitCSASummariesManagerFeedback);
      setSubmitManagerFeedbackState(DataState.SUCCESS);
    }
  } catch (e) {
    logger.error('Error in submitManagerFeedback', e as Error);
    const errorData = {
      error: (e as Error).message,
    };
    setSubmitManagerFeedback(errorData);
    setSubmitManagerFeedbackState(DataState.ERROR);
  }

  // refetch csa query as TM may have cached this earlier by viewing CSA's dashboard / summaries on team page
  // if we are on the most recent apollo client, this is given to us out of the box
  // we can't refetch getTeamWeeklySummaries as we aren't guaranteed the same order of direct reports
  // so getTeamWeeklySummaries, will not be cached.
  const refetchCSAVariables = {
    alias: variables.csaAlias,
    limit: 1,
    managerAlias: variables.alias,
  };
  getCSAWeeklySummaries(
    refetchCSAVariables,
    () => {},
    () => {},
    'network-only'
  );
};

export default {
  addCoachingComment,
  getAppNotifications,
  updateAppNotification,
  sendNotification,
  prepareUpdateAppNotificationInput,
  prepareQueryForUpdateNotification,
  prepareQueryForGetNotification,
  getInspireOptions,
  getCoachingNotes,
  getContactRecommendations,
  prepareQuery,
  prepareSimpleTeamProfileQuery,
  prepareQueryForCustomerComment,
  prepareQueryForAddCoachingComment,
  prepareQueryForGetCoachingComments,
  prepareQueryForRemoveCoachee,
  prepareQueryForRemoveCoach,
  prepareQueryForAddCoach,
  prepareQueryForWriteOutlierException,
  prepareQueryForUpdateOutlierException,
  writeOutlierException,
  updateOutlierException,
  updateContactRecommendation,
  prepareQueryforRecommendationsGetter,
  prepareAssociateQuery,
  prepareOrgVerticalQuery,
  prepareContactQuery,
  prepareContactDetailsQuery,
  removeKeys,
  getProfileData,
  getContacts,
  getTeamProfileData,
  getCustomerCommentsData,
  getSingleCoachingNote,
  getCoachingHistory,
  getCoachingComments,
  updateCoachingNote,
  removeCoachee,
  removeCoach,
  addCoach,
  getAssociateProfile,
  getOrgVertical,
  getDraftCoachingNote,
  getWeeklySummaryCoachingStatus,
  getLighthouseMetrics,
  getCustomLighthouseMetrics,
  prepareLighthouseQuery,
  prepareCustomLighthouseQuery,
  getTeamLighthouseMetrics,
  prepareTeamLighthouseQuery,
  getCustomTeamLighthouseMetrics,
  prepareCustomTeamLighthouseQuery,
  prepareLighthouseFiltersQuery,
  prepareLighthouseCustomFiltersQuery,
  getLighthouseFilters,
  getLighthouseCustomFilters,
  prepareLighthouseDefaultVerticalQuery,
  getDefaultVertical,
  prepareHmdDetailsQuery,
  getHMDDetails,
  prepareRealtimeHmdDetailsQuery,
  prepareContactStatusQuery,
  getContactStatus,
  getRealtimeHmdDetails,
  prepareQueryForRemoveProxy,
  prepareQueryForHideCustomerComment,
  prepareQueryForAddProxy,
  addProxy,
  removeProxy,
  hideCustomerComment,
  prepareProxyTeamLighthouseQuery,
  prepareProxyCustomTeamLighthouseQuery,
  prepareQueryForGetProxyNotification,
  prepareQueryForDeleteProxyNotification,
  getProxyNotification,
  deleteProxyNotification,
  getConsolidatedContacts,
  getFeedbackMessages,
  prepareContactListeningDataQuery,
  getContactListeningData,
  getContactListeningDataWithChat,
  updateFormReview,
  getFormTemplateData,
  getCCXDetailsData,
  prepareQueryForGetCoachingStats,
  prepareQueryForBatchGetCoachingStats,
  getCoachingStats,
  getBatchCoachingStats,
  prepareQueryforGetCoachingsAndContactReviews,
  getCoachingsAndContactReviews,
  getCCXForContactData,
  getTrendsMetrics,
  prepareTrendsMetricsQuery,
  getTeamDetailsMetrics,
  prepareTeamDetailsMetricsQuery,
  createGoal,
  prepareCreateGoalInput,
  prepareQueryforGetTeamLevelGoals,
  getTeamLevelGoals,
  prepareQueryforGetAssociateLevelGoals,
  getAssociateLevelGoals,
  prepareGoalJustificationInput,
  addGoalJustification,
  prepareQueryForGetGoalComments,
  getGoalComments,
  prepareAddGoalCommentInput,
  addGoalComment,
  prepareQueryForGetAssociateInsights,
  getAssociateInsights,
  getTrainingStats,
  prepareTeamProfileDataQuery,
  prepareQueryForGetTeamProxy,
  getTeamProxy,
  getCSAWeeklySummaries,
  submitCSASummariesManagerFeedback,
};
